import { flowArrow, formatCurrency, formatNetAmount } from "@/utils/formatters";

export interface IncomeExpenseWeek {
  id: string;
  label: string;
  income: number;
  expense: number;
  /** Semana en curso: muestra la etiqueta `currentWeekLabel`. */
  current?: boolean;
}

export interface IncomeExpenseCardProps {
  /** Ingreso del ciclo (positivo). */
  income: number;
  /** Gasto del ciclo (positivo). */
  expense: number;
  /** Semanas del ciclo (solo se ven en pantallas md+). */
  weeks: IncomeExpenseWeek[];
  /** Rangos de semanas sin movimientos, p. ej. "1–7 sep". */
  emptyWeeks: string[];
  netLabel: string;
  inflowLabel: string;
  outflowLabel: string;
  currentWeekLabel: string;
  /**
   * Resumen de semanas vacías. Con función recibe el número de semanas y sus rangos;
   * con string se muestra tal cual.
   */
  emptyWeeksLabel: string | ((count: number, ranges: string[]) => string);
}

const netText = (n: number) => formatNetAmount(n, { plus: false });

const toneClass = (n: number) =>
  n < 0 ? "text-[var(--app-danger)]" : n > 0 ? "text-[var(--app-success)]" : "text-[var(--app-text-secondary)]";

const flowText = (n: number, flow: "income" | "expense") => (n === 0 ? formatCurrency(0) : `${flowArrow(flow)} ${formatCurrency(n)}`);

/**
 * Ingresos contra gastos del ciclo en tarjeta neutra. Pantallas md+: totales, barra de
 * proporción y desglose por semana. Móvil: barra y leyenda.
 */
export function IncomeExpenseCard({
  income,
  expense,
  weeks,
  emptyWeeks,
  netLabel,
  inflowLabel,
  outflowLabel,
  currentWeekLabel,
  emptyWeeksLabel,
}: IncomeExpenseCardProps) {
  const total = income + expense || 1;
  const net = income - expense;
  const maxFlow = Math.max(1, ...weeks.flatMap((w) => [w.income, w.expense]));
  const incomePct = Math.round((income / total) * 100);
  const expensePct = Math.round((expense / total) * 100);

  const bar = (
    <div
      className="flex h-3 gap-1 md:h-4"
      role="img"
      aria-label={`${inflowLabel} ${formatCurrency(income)} · ${outflowLabel} ${formatCurrency(expense)}`}
    >
      <i className="block h-full rounded-l-[6px] rounded-r-[2px] bg-[var(--app-success)] md:rounded-l-[7px]" style={{ width: `${incomePct}%` }} />
      <i
        className="block h-full min-w-2 rounded-l-[2px] rounded-r-[6px] bg-[var(--app-danger)] md:rounded-r-[7px]"
        style={{ width: `${expensePct}%` }}
      />
    </div>
  );

  const emptyText =
    typeof emptyWeeksLabel === "function" ? emptyWeeksLabel(emptyWeeks.length, emptyWeeks) : emptyWeeksLabel;

  return (
    <div className="rounded-[var(--ds-radius-lg)] bg-[var(--app-card)] px-5 py-4 text-[var(--app-text)] md:p-6">
      <div className="text-[11px] font-medium leading-4 tracking-[0.18em] text-[var(--app-text-secondary)] uppercase">
        {netLabel}
      </div>
      <div className={`mt-0.5 font-[var(--font-display)] text-[28px] leading-none font-medium tracking-[-0.03em] md:mt-2 md:text-[36px] ${toneClass(net)}`}>
        {netText(net)}
      </div>

      {/* Móvil: barra y leyenda */}
      <div className="md:hidden">
        {bar}
        <div className="mt-2 flex justify-between text-[13px] leading-5">
          <span className="inline-flex items-center gap-2">
            <i aria-hidden="true" className="inline-block size-3 rounded-full bg-[var(--app-success)]" />
            {inflowLabel} {flowText(income, "income")}
          </span>
          <span className="inline-flex items-center gap-2">
            <i aria-hidden="true" className="inline-block size-3 rounded-full bg-[var(--app-danger)]" />
            {outflowLabel} {flowText(expense, "expense")}
          </span>
        </div>
      </div>

      {/* Web: totales, barra y semanas */}
      <div className="hidden md:block">
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm leading-5">
          <span className="inline-flex items-center gap-2">
            <i aria-hidden="true" className="inline-block size-3 rounded-full bg-[var(--app-success)]" />
            {inflowLabel} {flowText(income, "income")}
          </span>
          <span className="inline-flex items-center gap-2">
            <i aria-hidden="true" className="inline-block size-3 rounded-full bg-[var(--app-danger)]" />
            {outflowLabel} {flowText(expense, "expense")}
          </span>
        </div>
        <div className="mt-4">{bar}</div>

        {weeks.map((week) => {
          const weekNet = week.income - week.expense;
          return (
            <div key={week.id} className="border-t border-[var(--app-border)] py-4">
              <div className="mb-3 flex items-center justify-between text-base leading-6 font-medium">
                <span className="inline-flex items-center">
                  {week.label}
                  {week.current && (
                    <span className="ml-2 inline-flex h-6 items-center rounded-full bg-[var(--ds-card-sun)] px-2 text-[11px] leading-none font-medium text-[var(--ds-on-card)]">
                      {currentWeekLabel}
                    </span>
                  )}
                </span>
                <span className={`text-[15px] leading-6 font-medium whitespace-nowrap ${weekNet === 0 ? "font-normal text-[var(--app-text-secondary)]" : toneClass(weekNet)}`}>
                  {netText(weekNet)}
                </span>
              </div>
              <FlowLine color="bg-[var(--app-success)]" width={(week.income / maxFlow) * 100} value={flowText(week.income, "income")} active={week.income > 0} tone="text-[var(--app-success)]" />
              <FlowLine color="bg-[var(--app-danger)]" width={(week.expense / maxFlow) * 100} value={flowText(week.expense, "expense")} active={week.expense > 0} tone="text-[var(--app-danger)]" />
            </div>
          );
        })}

        {emptyWeeks.length > 0 && (
          <div className="mt-4 rounded-2xl border-[1.5px] border-dashed border-[var(--app-border)] px-4 py-3 text-sm leading-5 text-[var(--app-text-secondary)]">
            {emptyText}
          </div>
        )}
      </div>
    </div>
  );
}

function FlowLine({ color, width, value, active, tone }: { color: string; width: number; value: string; active: boolean; tone: string }) {
  return (
    <div className="mt-2 flex items-center gap-3">
      <span className="h-3.5 flex-1 overflow-hidden rounded-[7px] bg-[var(--app-border)]" aria-hidden="true">
        <i className={`block h-full rounded-[7px] ${color}`} style={{ width: `${width}%` }} />
      </span>
      <span className={`w-28 text-right text-sm leading-5 font-medium whitespace-nowrap ${active ? tone : "font-normal text-[var(--app-text-secondary)]"}`}>
        {value}
      </span>
    </div>
  );
}
